import { useSelector } from "react-redux";
import { useLocation, Link } from "react-router-dom";
import constants from "../common/constants";
import {
  AppBar, Box, IconButton, LinearProgress, List, ListItemButton,
  ListItemIcon, Tooltip, Typography,
} from "@mui/material";
import GridViewIcon from "@mui/icons-material/GridView";
import CollectionsIcon from "@mui/icons-material/Collections";
import PersonIcon from "@mui/icons-material/Person";
import FaceIcon from "@mui/icons-material/Face";
import LinkIcon from "@mui/icons-material/Link";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useApi } from "../hooks";
import { apiLogutUser, method } from "../apiPath";
import { getToast } from "../common/utils";
import keyNames from "../keyName";

const { ADMIN, USER } = constants;
const toast = getToast("error");

const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  const gb = bytes / 1024 ** 3;
  if (gb >= 1) return `${gb.toFixed(1)} GB`;
  const mb = bytes / 1024 ** 2;
  if (mb >= 1) return `${mb.toFixed(0)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
};

const NAV_SECTIONS = [
  {
    label: "MAIN",
    items: [
      { key: "dashboard",  label: "Dashboard",    link: "/app/dashboard",  icon: <GridViewIcon sx={{ fontSize: 18 }} />,      roles: [ADMIN] },
      { key: "gallery",    label: "Gallery",       link: "/app/gallery",    icon: <CollectionsIcon sx={{ fontSize: 18 }} />,   roles: [ADMIN] },
      { key: "tracking",   label: "Shared Links",  link: "/app/tracking",   icon: <LinkIcon sx={{ fontSize: 18 }} />,          roles: [ADMIN] },
      { key: "my-photos",  label: "My Photos",     link: "/app/my-photos",  icon: <FaceIcon sx={{ fontSize: 18 }} />,          roles: [USER] },
    ],
  },
  {
    label: "MANAGE",
    items: [
      { key: "profile",    label: "Profile",       link: "/app/profile",    icon: <PersonIcon sx={{ fontSize: 18 }} />,        roles: [ADMIN] },
    ],
  },
];

const AdminSidebar = ({ open, isMobile, setOpen }) => {
  const { userData, dashboardStats } = useSelector((state) => state.mainReducer);
  const location = useLocation();
  const hitApi = useApi();
  const isActive = (link) => location.pathname.startsWith(link);

  const logOut = () => hitApi(apiLogutUser, null, method.post, keyNames.userData, toast);

  const initials = userData
    ? `${userData.firstName?.[0] || ""}${userData.lastName?.[0] || ""}`.toUpperCase()
    : "?";

  const storageUsed = dashboardStats?.storageUsed || 0;
  const storagePercent = Math.min((storageUsed / (10 * 1024 ** 3)) * 100, 100);

  return (
    <AppBar
      position="fixed"
      component="div"
      color="transparent"
      elevation={0}
      sx={{
        width: isMobile ? 240 : (open ? 240 : 66),
        right: "inherit",
        height: "calc(100% - 64px)",
        top: 64,
        background: "#ffffff",
        boxShadow: "2px 0 16px rgba(232,42,42,0.08)",
        borderRight: "1px solid rgba(232,42,42,0.14)",
        borderRadius: "0 !important",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        zIndex: 1200,
        transition: isMobile ? "transform 0.25s ease" : "width 0.25s ease",
        transform: isMobile ? (open ? "translateX(0)" : "translateX(-100%)") : "none",
      }}
    >
      {/* Brand strip */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: open ? "flex-start" : "center",
          gap: 1.5,
          px: open ? 2.5 : 0,
          py: 1.8,
          borderBottom: "1px solid rgba(232,42,42,0.12)",
          background: "#fff",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 32, height: 32, borderRadius: "10px",
            background: "linear-gradient(135deg, #E82A2A, #FF6B6B)",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
            boxShadow: "0 0 16px rgba(232,42,42,0.30)",
          }}
        >
          <GridViewIcon sx={{ color: "#fff", fontSize: 17 }} />
        </Box>
        {open && (
          <Box>
            <Typography sx={{ color: "#111827", fontWeight: 800, fontSize: "0.92rem", lineHeight: 1 }}>
              Gallery
            </Typography>
            <Typography
              sx={{
                fontSize: "0.55rem", fontWeight: 700, letterSpacing: "2px",
                textTransform: "uppercase", color: "#E82A2A", lineHeight: 1.4,
              }}
            >
              Panel
            </Typography>
          </Box>
        )}
      </Box>

      {/* Nav sections */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", py: 1 }}>
        {NAV_SECTIONS.map((section) => {
          const sectionItems = section.items.filter((i) => i.roles.includes(userData?.role));
          if (!sectionItems.length) return null;

          return (
            <Box key={section.label}>
              {open && (
                <Typography
                  sx={{
                    fontSize: "0.62rem", fontWeight: 700, letterSpacing: "1.5px",
                    textTransform: "uppercase", color: "#cc7a7a",
                    px: 2.5, pt: 1.5, pb: 0.5,
                  }}
                >
                  {section.label}
                </Typography>
              )}
              <List disablePadding sx={{ px: 1 }}>
                {sectionItems.map((item) => {
                  const active = isActive(item.link);
                  const button = (
                    <ListItemButton
                      key={item.key}
                      component={Link}
                      to={item.link}
                      onClick={() => isMobile && setOpen(false)}
                      sx={{
                        mb: 0.4, borderRadius: "8px", minHeight: 42,
                        px: open ? 1.5 : 0,
                        justifyContent: open ? "flex-start" : "center",
                        position: "relative",
                        background: active
                          ? "linear-gradient(90deg, rgba(232,42,42,0.10) 0%, transparent 100%)"
                          : "transparent",
                        borderLeft: active ? "3px solid #E82A2A" : "3px solid transparent",
                        transition: "all 0.15s ease",
                        "&:hover": {
                          background: "#ffebeb",
                          borderLeft: active ? "3px solid #E82A2A" : "3px solid transparent",
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: open ? 36 : "auto",
                          color: active ? "#E82A2A" : "#9ca3af",
                          justifyContent: "center",
                          transition: "color 0.15s",
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {open && (
                        <Typography
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: active ? 700 : 500,
                            color: active ? "#E82A2A" : "#374151",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.label}
                        </Typography>
                      )}
                    </ListItemButton>
                  );

                  return open ? button : (
                    <Tooltip key={item.key} title={item.label} placement="right" arrow>
                      {button}
                    </Tooltip>
                  );
                })}
              </List>
            </Box>
          );
        })}
      </Box>

      {/* ── User footer ── */}
      <Box sx={{ borderTop: "1px solid rgba(232,42,42,0.12)", flexShrink: 0, background: "#fff" }}>
        {/* Storage bar — only when expanded and data available */}
        {open && storageUsed > 0 && (
          <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.6 }}>
              <Typography sx={{ fontSize: "0.62rem", fontWeight: 600, color: "#cc7a7a", letterSpacing: "0.5px" }}>
                Storage Used
              </Typography>
              <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "#6a2d2d" }}>
                {formatBytes(storageUsed)}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={storagePercent}
              sx={{
                height: 5, borderRadius: 99, background: "#ffc9c9",
                "& .MuiLinearProgress-bar": {
                  background: "linear-gradient(90deg, #E82A2A, #FF6B6B)",
                  borderRadius: 99,
                },
              }}
            />
          </Box>
        )}

        {/* User row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: open ? 1.2 : 0,
            px: open ? 1.5 : 0,
            py: 1.2,
            justifyContent: open ? "flex-start" : "center",
          }}
        >
          {/* Avatar */}
          <Box
            sx={{
              width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
              background: "linear-gradient(135deg, #E82A2A, #FF6B6B)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontWeight: 700, fontSize: "0.78rem",
              overflow: "hidden",
            }}
          >
            {userData?.profileImage ? (
              <Box
                component="img"
                src={userData.profileImage}
                alt={initials}
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              initials || <AccountCircleIcon sx={{ fontSize: 18 }} />
            )}
          </Box>

          {open && (
            <>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                  fontSize: "0.78rem", fontWeight: 700, color: "#111827",
                  lineHeight: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {userData?.firstName} {userData?.lastName}
                </Typography>
                <Typography sx={{ fontSize: "0.62rem", color: "#cc7a7a", lineHeight: 1.5 }}>
                  {userData?.role === "CRMUT5022" ? "Super Admin" : "User"}
                </Typography>
              </Box>

              <Tooltip title="Logout" placement="top">
                <IconButton
                  size="small"
                  onClick={logOut}
                  sx={{
                    color: "#cc7a7a", flexShrink: 0,
                    "&:hover": { color: "#E82A2A", background: "rgba(232,42,42,0.08)" },
                  }}
                >
                  <PowerSettingsNewIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>

      </Box>
    </AppBar>
  );
};

export default AdminSidebar;
